import { formatCurrency } from "@scandotcom/react";
import classNames from "classnames";
import React from "react";
import { AvailabilitySlot } from "./useAvailabilitySlots";
import { parseISO } from "date-fns";
import { isNil } from "../../utils";

interface AvailabilityItemProps
  extends Omit<React.ComponentProps<"button">, "slot"> {
  slot: AvailabilitySlot;
  selected?: boolean;
  nextDaySurcharge: number | null;
}

const timeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeStyle: "short",
});

export function AvailabilityItem({
  slot,
  disabled,
  selected,
  nextDaySurcharge,
  ...props
}: AvailabilityItemProps) {
  const formattedTime = timeFormatter.format(parseISO(slot.start_time));

  const formattedExtraCost = !isNil(nextDaySurcharge)
    ? formatCurrency(nextDaySurcharge / 100)
    : null;

  const costsStyles =
    "text-xs text-neutral-500 font-normal group-disabled:text-neutral-400 group-disabled:font-medium";

  const buttonStyles = classNames(
    "group min-w-[100px] rounded-md px-4 py-3 text-center text-sm font-medium",
    "border border-neutral-300 shadow-sm ring-primary-200 transition-colors text-neutral-950",
    "active:bg-primary-300 active:border-neutral-950",
    "focus-visible:border-neutral-900 focus-visible:outline-none focus-visible:ring-2",
    "disabled:bg-neutral-100 disabled:text-neutral-500 disabled:border-neutral-200",
    selected
      ? "bg-primary-300 border-neutral-950"
      : "hover:bg-neutral-50 bg-white"
  );

  return (
    <button
      data-testid="availability-item"
      {...props}
      disabled={disabled}
      type="button"
      className={buttonStyles}
    >
      <time dateTime={slot.start_time}>{formattedTime}</time>
      {formattedExtraCost && (
        <span
          className={classNames(
            costsStyles,
            "max-sm:ml-1 max-sm:before:content-['('] max-sm:after:content-[')'] sm:-mt-1 sm:block"
          )}
        >
          +{formattedExtraCost}
        </span>
      )}
    </button>
  );
}
